import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import type { AdminEmailSuppressionEntry } from "@a7y/types";
import { EMAIL_CATEGORY_LABELS } from "@a7y/types";
import type { EmailCategory } from "@a7y/types";
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@a7y/tailwind-catalyst";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Link2,
  MailX,
  Search,
  ShieldCheck,
  ShieldOff,
  X,
} from "lucide-react";
import { type ChangeEvent, useRef, useState } from "react";

const PAGE_SIZE = 20;

export function EmailSuppressionsPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [statusMessage, setStatusMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 400);
  };

  const { data, isLoading } = useQuery({
    queryKey: ["email-suppressions", { page, search: debouncedSearch }],
    queryFn: () =>
      api.getEmailSuppressions({
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        search: debouncedSearch || undefined,
      }),
  });

  const entries: AdminEmailSuppressionEntry[] = (data?.data ?? []) as AdminEmailSuppressionEntry[];
  const total = data?.total ?? 0;

  const showStatus = (type: "success" | "error", text: string) => {
    setStatusMessage({ type, text });
    setTimeout(() => setStatusMessage(null), 4000);
  };

  // Mutations
  const suppressMutation = useMutation({
    mutationFn: (email: string) => api.suppressEmail(email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-suppressions"] });
      showStatus("success", "Email suppressed");
    },
    onError: () => showStatus("error", "Failed to suppress email"),
  });

  const unsuppressMutation = useMutation({
    mutationFn: (email: string) => api.unsuppressEmail(email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-suppressions"] });
      showStatus("success", "Suppression removed");
    },
    onError: () => showStatus("error", "Failed to unsuppress email"),
  });

  const generateLinkMutation = useMutation({
    mutationFn: (email: string) => api.generatePreferenceLink(email),
    onSuccess: async (data) => {
      try {
        await navigator.clipboard.writeText(data.url);
        showStatus("success", "Preference link copied to clipboard");
      } catch {
        // Clipboard API may fail in some contexts; show the URL in status
        showStatus("success", `Link: ${data.url}`);
      }
    },
    onError: () => showStatus("error", "Failed to generate preference link"),
  });

  const optedOutCategories = (
    optedOut: Partial<Record<EmailCategory, boolean>>,
  ): EmailCategory[] =>
    (Object.entries(optedOut) as [EmailCategory, boolean][])
      .filter(([, v]) => v)
      .map(([k]) => k);

  return (
    <div className="px-4 py-0">
      <PageHeader
        title="Email Suppressions"
        subtitle="Manage email preference suppressions and generate preference links"
      />

      {/* Search */}
      <div className="mb-6">
        <div className="relative w-[340px]">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            autoComplete="off"
            placeholder="Search by email address..."
            value={search}
            onChange={handleSearchChange}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-10 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-zinc-600 dark:bg-zinc-800 dark:text-white"
          />
          {search && (
            <button
              onClick={() => {
                setSearch("");
                setDebouncedSearch("");
                setPage(1);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Status banner */}
      {statusMessage && (
        <div
          className={`mb-4 rounded-lg px-4 py-2.5 text-sm font-medium ${
            statusMessage.type === "success"
              ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {statusMessage.text}
        </div>
      )}

      {/* Table */}
      <Table dense striped>
        <TableHead>
          <TableRow>
            <TableHeader>Email</TableHeader>
            <TableHeader>Global Unsubscribe</TableHeader>
            <TableHeader>Categories Opted Out</TableHeader>
            <TableHeader>Last Updated</TableHeader>
            <TableHeader className="text-right">Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center">
                <div className="mx-auto h-6 w-6 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 dark:border-zinc-600" />
              </TableCell>
            </TableRow>
          ) : entries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="py-8 text-center">
                <MailX className="mx-auto h-12 w-12 text-zinc-400" />
                <p className="mt-2 text-zinc-500">
                  {debouncedSearch
                    ? "No suppressions found matching your search"
                    : "No email suppressions found"}
                </p>
              </TableCell>
            </TableRow>
          ) : (
            entries.map((entry) => {
              const cats = optedOutCategories(entry.optedOut);
              return (
                <TableRow key={entry.email}>
                  <TableCell className="font-medium font-mono text-sm">
                    {entry.email}
                  </TableCell>
                  <TableCell>
                    {entry.globalUnsubscribe ? (
                      <Badge color="red">Unsubscribed</Badge>
                    ) : (
                      <Badge color="green">Active</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {cats.length === 0 ? (
                      <span className="text-zinc-400 text-sm">None</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {cats.map((cat) => (
                          <Badge key={cat} color="amber">
                            {EMAIL_CATEGORY_LABELS[cat].name}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-zinc-500 text-sm">
                    {formatDate(entry.updatedAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {entry.globalUnsubscribe ? (
                        <Button
                          plain
                          onClick={() =>
                            unsuppressMutation.mutate(entry.email)
                          }
                          disabled={unsuppressMutation.isPending}
                          title="Remove suppression"
                        >
                          <ShieldCheck className="h-4 w-4 text-green-500" />
                        </Button>
                      ) : (
                        <Button
                          plain
                          onClick={() =>
                            suppressMutation.mutate(entry.email)
                          }
                          disabled={suppressMutation.isPending}
                          title="Suppress email"
                        >
                          <ShieldOff className="h-4 w-4 text-red-400" />
                        </Button>
                      )}
                      <Button
                        plain
                        onClick={() =>
                          generateLinkMutation.mutate(entry.email)
                        }
                        disabled={generateLinkMutation.isPending}
                        title="Copy preference link"
                      >
                        <Link2 className="h-4 w-4 text-blue-400" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="mt-6 flex items-center justify-between">
          <Text className="text-sm text-gray-700 dark:text-zinc-300">
            Showing{" "}
            <span className="font-medium">
              {(page - 1) * PAGE_SIZE + 1}
            </span>{" "}
            to{" "}
            <span className="font-medium">
              {Math.min(page * PAGE_SIZE, total)}
            </span>{" "}
            of <span className="font-medium">{total}</span> results
          </Text>
          <div className="flex gap-2">
            <Button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              outline
            >
              Previous
            </Button>
            <Button
              onClick={() => setPage(page + 1)}
              disabled={page * PAGE_SIZE >= total}
              outline
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
